/////////////////////////////////////////

/**
 * This is the official, public spec for Metadata written to the ARP registry.
 *
 * @param title title of the media
 * @param description optional description of the media
 * @param bpm title of the media
 * @param key key of the media, represented following the schema defined in KeyAndScale.ts
 * @param genres genre labels for the media (e.g. "Rock")
 * @param tags tags for searching and filtering. For one-shot samples, this is used for intrument type (e.g. "Kick").
 * @param mediaType string representing the type of media (e.g. "song", "stem", or "one-shot")
 */
export type RegisteredArpMetadata = {
  title: string
  bpm: number
  key: number
  genres: string[]
  tags: string[]
  mediaType: string
  access?: ArpAccess
  samplePackTitle?: string
}

/**
 * A reference to any ERC-721 compliant token on any EVM-compatible chain.
 *
 * @param tokenId token ID on the token contract
 * @param contractAddress address of the ERC-721 token contract.
 * @param originType PRIMARY or SECONDARY,
 */
export type OriginToken = {
  tokenId: number
  chainId: number
  contractAddress: string
  originType: OriginType
}

/**
 * Indicates whether this is the primary media that corresponds to
 * a referenced token, so that when a user calls `getMediaByOrigin()`, ARP will
 * only return the PRIMARY media and ignore the SECNODARY ones.
 * There can only be one PRIMARY media registered in ARP for each ERC-721 token.
 */
export enum OriginType {
  PRIMARY,
  SECONDARY
}

///////////////////////////////////////////
// Internal data models related to ARP ////
///////////////////////////////////////////

/**
 * @param artistEthAddress address of the artist who created this media
 * @param dataUri URL of the actual data
 * @param cachedWavDataUri optional cached wav URL of the actual data
 * @param cachedMp3DataUri optional cached mp3 URL of the actual data
 * @param metadata describes the metadata
 * @param originToken optionally stores a reference to an existing unique ERC-721 token representing this media. If no token exists, set to null.
 * @param subcomponents array of ARP IDs of subcomponents used in this media (e.g. list of samples used in a stem)
 * @param create_time epoch millis of the creation time of this media
 * @param version version of the item that was stored on the ARP smart contract
 */
export type InternalArpMedia = {
  artistEthAddress: string
  dataUri: string
  cachedWavDataUri?: string
  cachedMp3DataUri?: string
  index: number
  metadata: InternalArpMetadata
  durationMs?: number
  originToken?: OriginToken
  subcomponents: number[]
  create_time?: number
  createTime?: number
  version?: number
}

/**
 * Includes the RegisteredArpMetadata, plus some additional metadata that's useful for storing
 * in our internal databases (but not on chain). This additional data includes arpeggiComposition.
 *
 * All fields are the same as above, except:
 * @param mediaType
 * @param arpeggiComposition optional stringified ArpeggiCompositionV5 composition data
 */
export type InternalArpMetadata = {
  title: string
  description?: string
  bpm: number
  key: number
  genres: string[]
  tags: string[]
  mediaType?: string
  subType?: string
  arpeggiComposition?: string
  access?: ArpAccess
  samplePackTitle?: string
}

/**
 * The access required to make this Arp Media visible to the end user
 *
 * STANDARD == open and available for all
 * TOKEN_GATE_V1 == token gated, requires ownership of sample pack edition
 * UPLOAD_TOKEN_GATE == only uploading media is token gated -- samples can be viewed and downloaded
 */
export enum ArpAccess {
  STANDARD = 'standard',
  TOKEN_GATE_V1 = 'token-gate-v1',
  UPLOAD_TOKEN_GATE = 'upload-token-gate'
}
